import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ZonePolygonEditor from './ZonePolygonEditor';
import { normalizePolygonFromApi, serializePolygonForApi } from '../../utils/polygonUtils';

/**
 * Composant d'édition/création d'une zone de livraison
 * Combine le formulaire et l'éditeur de polygone
 */
const ZoneLivraisonEditor = ({ 
  zone = null, // Zone existante en mode édition, null en mode création
  commercantId,
  defaultCenter = null, // [lat, lng] - Centre par défaut (adresse du commerçant)
  existingZones = [], // Zones existantes à afficher en référence (pour la création de nouvelles zones)
  onSave,
  onCancel
}) => {
  // Key unique basée sur la zone pour forcer le remount seulement quand nécessaire
  // Utiliser useRef pour garder la même key pendant toute la durée de vie du composant
  const modalKeyRef = useRef(zone?.id ? `zone-${zone.id}` : `new-${Date.now()}`);
  
  // Mettre à jour la key seulement si la zone change vraiment
  useEffect(() => {
    const newKey = zone?.id ? `zone-${zone.id}` : `new-${Date.now()}`;
    if (modalKeyRef.current !== newKey) {
      modalKeyRef.current = newKey;
    }
  }, [zone?.id]);
  
  const modalKey = modalKeyRef.current;
  
  const [formData, setFormData] = useState({
    nom: '',
    polygon: null,
    montantMinimum: '',
    frais: '',
    livraisonGratuite: '',
    delaiMin: '',
    delaiMax: '',
    priorite: '0',
    actif: true
  });

  // Initialiser le formulaire avec les données de la zone en mode édition
  // ET reset complet à chaque changement de zone (pour éviter les résidus)
  useEffect(() => {
    
    if (zone) {
      setFormData({
        nom: zone.nom || '',
        polygon: zone.polygon || null,
        montantMinimum: zone.montantMinimumCents ? (zone.montantMinimumCents / 100).toFixed(2) : '',
        frais: zone.fraisCents ? (zone.fraisCents / 100).toFixed(2) : '',
        livraisonGratuite: zone.livraisonGratuiteCents ? (zone.livraisonGratuiteCents / 100).toFixed(2) : '',
        delaiMin: zone.delaiMin?.toString() || '',
        delaiMax: zone.delaiMax?.toString() || '',
        priorite: zone.priorite?.toString() || '0',
        actif: zone.actif !== false
      });
    } else {
      // Réinitialiser complètement le formulaire en mode création
      setFormData({
        nom: '',
        polygon: null,
        montantMinimum: '',
        frais: '',
        livraisonGratuite: '',
        delaiMin: '',
        delaiMax: '',
        priorite: '0',
        actif: true
      });
    }
  }, [zone]);

  // Gérer les changements du polygone (mémorisé pour éviter les re-renders)
  const handlePolygonChange = useCallback((polygon) => {
    setFormData(prev => ({ ...prev, polygon }));
  }, []);

  // Gérer la soumission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Valider le formulaire
    if (!formData.nom.trim()) {
      alert('Le nom de la zone est requis');
      return;
    }

    if (formData.polygon && formData.polygon.length < 3) {
      alert('Un polygone doit contenir au moins 3 points');
      return;
    }

    // Préparer les données pour l'API
    const zoneData = {
      nom: formData.nom.trim(),
      polygon: serializePolygonForApi(formData.polygon),
      montantMinimumCents: formData.montantMinimum ? Math.round(parseFloat(formData.montantMinimum) * 100) : 0,
      fraisCents: formData.frais ? Math.round(parseFloat(formData.frais) * 100) : 0,
      livraisonGratuiteCents: formData.livraisonGratuite 
        ? Math.round(parseFloat(formData.livraisonGratuite) * 100) 
        : null,
      delaiMin: formData.delaiMin ? parseInt(formData.delaiMin, 10) : 30,
      delaiMax: formData.delaiMax ? parseInt(formData.delaiMax, 10) : 60,
      priorite: parseInt(formData.priorite, 10) || 0,
      actif: formData.actif
    };

    if (onSave) {
      onSave(zoneData);
    }
  };

  // Centre initial de la carte (ne change pas pendant le dessin pour éviter les décalages)
  const initialCenterRef = useRef(null);
  
  // Réinitialiser le centre quand la zone change (pour forcer le recalcul)
  useEffect(() => {
    initialCenterRef.current = null;
  }, [zone?.id]); // Réinitialiser quand on change de zone
  
  // Calculer le centre pour la carte (mémorisé pour éviter les re-renders)
  const mapCenter = useMemo(() => {
    // Si on a déjà un centre initial, le garder (pour éviter les décalages pendant le dessin)
    if (initialCenterRef.current) {
      return initialCenterRef.current;
    }
    
    // Calculer le centre initial une seule fois
    let calculatedCenter = null;
    
    // PRIORITÉ 1: Si on édite une zone existante, calculer le centre de SON polygone
    if (zone && zone.polygon) {
      const normalized = normalizePolygonFromApi(zone.polygon);
      if (normalized.length > 0) {
        const sum = normalized.reduce(
          (acc, point) => ({ lat: acc.lat + point.lat, lng: acc.lng + point.lng }),
          { lat: 0, lng: 0 }
        );
        calculatedCenter = [sum.lat / normalized.length, sum.lng / normalized.length];
      }
    }
    
    // PRIORITÉ 2: Calculer le centre de toutes les zones existantes pour centrer la carte dessus
    if (!calculatedCenter && existingZones && existingZones.length > 0) {
      const allPolygonPoints = existingZones
        .filter(z => z.polygon)
        .map(z => normalizePolygonFromApi(z.polygon))
        .flat();
      
      if (allPolygonPoints.length > 0) {
        const sum = allPolygonPoints.reduce(
          (acc, point) => ({ lat: acc.lat + point.lat, lng: acc.lng + point.lng }),
          { lat: 0, lng: 0 }
        );
        calculatedCenter = [sum.lat / allPolygonPoints.length, sum.lng / allPolygonPoints.length];
      }
    }
    
    // PRIORITÉ 3: Utiliser l'adresse du commerçant si fournie
    if (!calculatedCenter && defaultCenter && Array.isArray(defaultCenter) && defaultCenter.length === 2) {
      calculatedCenter = defaultCenter;
    }
    
    // Fallback sur Paris
    if (!calculatedCenter) {
      calculatedCenter = [48.8566, 2.3522];
    }
    
    // Stocker le centre initial
    initialCenterRef.current = calculatedCenter;
    return calculatedCenter;
  }, [zone, existingZones, defaultCenter]); // Inclure zone pour recalculer quand on change de zone

  // Calculer le zoom (mémorisé)
  const mapZoom = useMemo(() => {
    // Si on édite une zone existante avec un polygone, zoomer pour voir cette zone
    if (zone && zone.polygon) {
      return 14; // Zoom plus proche pour voir la zone en détail
    }
    // Si on a un polygone en cours de dessin
    if (formData.polygon && formData.polygon.length > 0) {
      return 13;
    }
    // Si on a des zones existantes, zoomer pour les voir toutes
    if (existingZones && existingZones.length > 0) {
      return 12; // Zoom plus large pour voir toutes les zones
    }
    return defaultCenter ? 14 : 12;
  }, [zone, formData.polygon, existingZones, defaultCenter]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6" style={{ position: 'relative', zIndex: 1 }}>
      {/* Informations de base */}
      <div className="space-y-4" style={{ position: 'relative', zIndex: 2 }}>
        <h3 className="text-lg font-semibold text-gray-900">
          {zone ? 'Modifier la zone' : 'Nouvelle zone de livraison'}
        </h3>

        <div style={{ position: 'relative', zIndex: 2 }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nom de la zone <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.nom}
            onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            placeholder="Ex: Centre-ville, Quartier Nord..."
            required
            style={{ position: 'relative', zIndex: 2 }}
          />
        </div>

        {/* Éditeur de polygone */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <label className="block text-sm font-medium text-gray-700 mb-2" style={{ position: 'relative', zIndex: 2 }}>
            Zone géographique (polygone)
          </label>
          <ZonePolygonEditor
            key={`zone-editor-${zone?.id || 'new'}-${modalKey}`} // Key unique qui change à chaque montage pour forcer le remount complet
            initialPolygon={formData.polygon}
            existingZones={existingZones} // Passer les zones existantes pour référence
            // IMPORTANT: key stable pour la carte Leaflet (change uniquement à l'ouverture de modale)
            mapInstanceKey={modalKey}
            onPolygonChange={handlePolygonChange}
            center={mapCenter}
            zoom={mapZoom}
            height="400px"
          />
        </div>

        {/* Tarification */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ position: 'relative', zIndex: 2 }}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant minimum (€)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.montantMinimum}
              onChange={(e) => setFormData({ ...formData, montantMinimum: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Frais de livraison (€)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.frais}
              onChange={(e) => setFormData({ ...formData, frais: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Livraison gratuite à partir de (€)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.livraisonGratuite}
              onChange={(e) => setFormData({ ...formData, livraisonGratuite: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optionnel"
            />
          </div>
        </div>

        {/* Délais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ position: 'relative', zIndex: 2 }}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Délai minimum (minutes)
            </label>
            <input
              type="number"
              min="0"
              value={formData.delaiMin}
              onChange={(e) => setFormData({ ...formData, delaiMin: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="30"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Délai maximum (minutes)
            </label>
            <input
              type="number"
              min="0"
              value={formData.delaiMax}
              onChange={(e) => setFormData({ ...formData, delaiMax: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="60"
            />
          </div>
        </div>

        {/* Priorité et statut */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ position: 'relative', zIndex: 2 }}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priorité (plus élevé = prioritaire)
            </label>
            <input
              type="number"
              value={formData.priorite}
              onChange={(e) => setFormData({ ...formData, priorite: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0"
            />
          </div>

          <div className="flex items-center pt-6">
            <input
              type="checkbox"
              id="actif"
              checked={formData.actif}
              onChange={(e) => setFormData({ ...formData, actif: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="actif" className="ml-2 block text-sm text-gray-900">
              Zone active
            </label>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200" style={{ position: 'relative', zIndex: 2 }}>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Annuler
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          {zone ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
};

export default ZoneLivraisonEditor;

